import { useState, useEffect, useRef, useCallback } from 'react';
import { useRecordContext } from 'react-admin';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AddIcon from '@mui/icons-material/Add';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { v4 } from 'uuid';
import { Storage } from 'shared/services/firebase-wrapper/FirebaseWrapper';
import { CampaignsMutationPayload } from '../models/campaigns-mutation-payload.model';

// ─── Types ────────────────────────────────────────────────────────────────────

interface MediaItem {
  id: string;
  url?: string;
  text: string;
  file?: File;
  preview?: string;
}

interface FormValues {
  name: string;
  description: string;
  is_active: boolean;
  start_date: string;
  end_date: string;
  gap_days: string;
  display_amount: string;
  headline: string;
  price_action_text: string;
  body_text: string;
  button_text: string;
  below_button_text: string;
}

type FormErrors = Partial<Record<keyof FormValues, string>>;

export interface CampaignsFormProps {
  onSave(values: Partial<CampaignsMutationPayload>): void;
  onCancel(): void;
  initialValues?: any;
  uid?: string;
  title: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const EMPTY_VALUES: FormValues = {
  name: '',
  description: '',
  is_active: false,
  start_date: '',
  end_date: '',
  gap_days: '',
  display_amount: '',
  headline: '',
  price_action_text: '',
  body_text: '',
  button_text: '',
  below_button_text: ''
};

function toDateInputValue(isoString: string): string {
  try {
    return new Date(isoString).toISOString().slice(0, 10);
  } catch {
    return '';
  }
}

function SectionCard({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card variant='outlined' sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant='subtitle1' fontWeight={600} mb={2}>
          {title}
        </Typography>
        {children}
      </CardContent>
    </Card>
  );
}

// ─── Rich Text Editor ─────────────────────────────────────────────────────────

const TOOLBAR = [
  ['bold', 'italic', 'underline', 'strike'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['link'],
  ['clean']
];

interface RichTextEditorProps {
  label: string;
  value: string;
  onChange(html: string): void;
  minHeight?: number;
}

function RichTextEditor({
  label,
  value,
  onChange,
  minHeight = 120
}: RichTextEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<Quill | null>(null);
  const onChangeRef = useRef(onChange);
  const lastHtmlRef = useRef(value);

  onChangeRef.current = onChange;

  useEffect(() => {
    if (!containerRef.current) return;

    const quill = new Quill(containerRef.current, {
      theme: 'snow',
      modules: {
        toolbar: TOOLBAR,
        clipboard: { matchVisual: false }
      }
    });

    quill.setContents(quill.clipboard.convert(value));
    lastHtmlRef.current = value;

    function handleChange() {
      const html =
        quill.root.innerHTML === '<p><br></p>' ? '' : quill.root.innerHTML;
      if (html !== lastHtmlRef.current) {
        lastHtmlRef.current = html;
        onChangeRef.current(html);
      }
    }

    quill.on('text-change', handleChange);
    quillRef.current = quill;

    return () => {
      quill.off('text-change', handleChange);
      quillRef.current = null;
    };
  }, []);

  // Sync when record loads in edit mode
  useEffect(() => {
    const quill = quillRef.current;
    if (!quill || value === lastHtmlRef.current) return;
    const selection = quill.getSelection();
    quill.setContents(quill.clipboard.convert(value));
    lastHtmlRef.current = value;
    if (selection && quill.hasFocus()) quill.setSelection(selection);
  }, [value]);

  return (
    <Box>
      <Typography
        variant='caption'
        color='text.secondary'
        sx={{ display: 'block', mb: 0.5 }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          '& .ql-toolbar.ql-snow': {
            borderRadius: '4px 4px 0 0',
            borderColor: 'rgba(0,0,0,0.23)',
            fontFamily: 'inherit'
          },
          '& .ql-container.ql-snow': {
            borderRadius: '0 0 4px 4px',
            borderColor: 'rgba(0,0,0,0.23)',
            fontSize: '1rem',
            fontFamily: 'inherit'
          },
          '& .ql-editor': { minHeight }
        }}
      >
        <div ref={containerRef} />
      </Box>
    </Box>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function CampaignsForm({
  onSave,
  onCancel,
  initialValues,
  uid,
  title
}: CampaignsFormProps) {
  const record = useRecordContext();

  const [values, setValues] = useState<FormValues>(EMPTY_VALUES);
  const [errors, setErrors] = useState<FormErrors>({});
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [mediaError, setMediaError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (mediaError && media.some((m) => m.url || m.file)) {
      setMediaError('');
    }
  }, [media, mediaError]);

  useEffect(() => {
    if (record) {
      const df = record.display_frequency ?? {};
      const dt = record.display_text ?? {};
      setValues({
        name: record.name ?? '',
        description: record.description ?? '',
        is_active: record.is_active ?? false,
        start_date: record.start_date
          ? toDateInputValue(record.start_date)
          : '',
        end_date: record.end_date ? toDateInputValue(record.end_date) : '',
        gap_days: df.gap_days ?? '',
        display_amount: df.display_amount ?? '',
        headline: dt.headline ?? '',
        price_action_text: dt.price_action_text ?? '',
        body_text: dt.text ?? '',
        button_text: dt.button ?? '',
        below_button_text: dt.below_button ?? ''
      });
      if (Array.isArray(record.media)) {
        setMedia(
          record.media.map((m: any) => ({
            id: v4(),
            url: m.url,
            text: m.text ?? ''
          }))
        );
      }
    } else if (initialValues) {
      const df = initialValues.display_frequency ?? {};
      setValues((prev) => ({
        ...prev,
        gap_days: String(df.gap_days ?? ''),
        display_amount: String(df.display_amount ?? '')
      }));
    }
  }, [record]);

  function setField<K extends keyof FormValues>(
    field: K,
    value: FormValues[K]
  ) {
    setValues((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  }

  const handleSubmit = useCallback(async () => {
    const errs: FormErrors = {};
    if (!values.name.trim()) {
      errs.name = 'Name is required';
    }
    if (!values.description.trim()) {
      errs.description = 'Description is required';
    }
    if (!values.start_date) {
      errs.start_date = 'Start date is required';
    }
    if (
      values.end_date &&
      values.start_date &&
      new Date(values.end_date) <= new Date(values.start_date)
    ) {
      errs.end_date = 'End date must be after start date';
    }
    if (!values.price_action_text.trim()) {
      errs.price_action_text = 'Price action text is required';
    }
    if (!values.button_text.trim()) {
      errs.button_text = 'Button CTA is required';
    }

    const hasImage = media.some((m) => m.url || m.file);
    if (!hasImage) {
      setMediaError('At least 1 image is required');
    }

    if (Object.keys(errs).length > 0 || !hasImage) {
      setErrors(errs);
      return;
    }

    setSaving(true);
    try {
      const campaignId = record?.id ?? uid;
      const processedMedia = await Promise.all(
        media.map(async (item) => {
          if (item.file) {
            const fileRef = ref(
              Storage,
              `monetization/campaigns/${campaignId}/${item.file.name + v4()}`
            );
            const snapshot = await uploadBytes(fileRef, item.file);
            const url = await getDownloadURL(snapshot.ref);
            return { url, type: 'image', text: item.text };
          }
          return { url: item.url, type: 'image', text: item.text };
        })
      );

      const payload = CampaignsMutationPayload.factory({
        name: values.name,
        description: values.description,
        is_active: values.is_active,
        start_date: values.start_date,
        end_date: values.end_date || undefined,
        display_frequency: {
          gap_days: values.gap_days,
          display_amount: values.display_amount
        },
        display_text: {
          headline: values.headline,
          price_action_text: values.price_action_text,
          text: values.body_text,
          button: values.button_text,
          below_button: values.below_button_text
        },
        media: processedMedia
      });

      onSave(payload);
    } finally {
      setSaving(false);
    }
  }, [values, media, record, uid, onSave]);

  // ─── Media Handlers ──────────────────────────────────────────────────────────

  function addMediaItem() {
    setMedia((prev) => [...prev, { id: v4(), text: '' }]);
  }

  function removeMediaItem(id: string) {
    setMedia((prev) => {
      const item = prev.find((m) => m.id === id);
      if (item?.preview) URL.revokeObjectURL(item.preview);
      return prev.filter((m) => m.id !== id);
    });
  }

  function moveMediaItem(index: number, direction: 'up' | 'down') {
    setMedia((prev) => {
      const next = [...prev];
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  function updateMediaText(id: string, text: string) {
    setMedia((prev) => prev.map((m) => (m.id === id ? { ...m, text } : m)));
  }

  function handleImageFile(id: string, file: File) {
    const preview = URL.createObjectURL(file);
    setMedia((prev) =>
      prev.map((m) =>
        m.id === id ? { ...m, file, preview, url: undefined } : m
      )
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
      <Typography variant='h5' fontWeight={600} mb={3}>
        {title}
      </Typography>

      <SectionCard title='Campaign Details'>
        <Stack spacing={2.5}>
          <TextField
            label='Name'
            value={values.name}
            onChange={(e) => setField('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            required
            fullWidth
          />
          <TextField
            label='Description'
            value={values.description}
            onChange={(e) => setField('description', e.target.value)}
            error={!!errors.description}
            helperText={errors.description}
            required
            fullWidth
            multiline
            rows={3}
          />
          <FormControlLabel
            control={
              <Switch
                checked={values.is_active}
                onChange={(e) => setField('is_active', e.target.checked)}
              />
            }
            label='Active'
          />
        </Stack>
      </SectionCard>

      <SectionCard title='Schedule'>
        <Stack direction='row' spacing={2}>
          <TextField
            label='Start Date'
            type='date'
            value={values.start_date}
            onChange={(e) => setField('start_date', e.target.value)}
            error={!!errors.start_date}
            helperText={errors.start_date}
            required
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label='End Date'
            type='date'
            value={values.end_date}
            onChange={(e) => setField('end_date', e.target.value)}
            error={!!errors.end_date}
            helperText={errors.end_date ?? 'Optional'}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Stack>
      </SectionCard>

      <SectionCard title='Display Frequency'>
        <Stack direction='row' spacing={2}>
          <TextField
            label='Display Amount'
            type='number'
            value={values.display_amount}
            onChange={(e) => setField('display_amount', e.target.value)}
            inputProps={{ min: 0 }}
            helperText='How many times to show'
            fullWidth
          />
          <TextField
            label='Gap Days'
            type='number'
            value={values.gap_days}
            onChange={(e) => setField('gap_days', e.target.value)}
            inputProps={{ min: 0 }}
            helperText='Days between appearances'
            fullWidth
          />
        </Stack>
      </SectionCard>

      <SectionCard title='Display Text'>
        <Stack spacing={2.5}>
          <TextField
            label='Headline'
            value={values.headline}
            onChange={(e) => setField('headline', e.target.value)}
            fullWidth
          />
          <TextField
            label='Price Action Text'
            value={values.price_action_text}
            onChange={(e) => setField('price_action_text', e.target.value)}
            error={!!errors.price_action_text}
            helperText={errors.price_action_text}
            required
            fullWidth
          />
          <RichTextEditor
            label='Body Text'
            value={values.body_text}
            onChange={(html) => setField('body_text', html)}
          />
          <Stack direction='row' spacing={2}>
            <TextField
              label='Button CTA'
              value={values.button_text}
              onChange={(e) => setField('button_text', e.target.value)}
              error={!!errors.button_text}
              helperText={errors.button_text}
              required
              fullWidth
            />
            <TextField
              label='Text Below Button'
              value={values.below_button_text}
              onChange={(e) => setField('below_button_text', e.target.value)}
              fullWidth
            />
          </Stack>
        </Stack>
      </SectionCard>

      <SectionCard title='Media'>
        <Stack spacing={2}>
          {mediaError && (
            <Typography variant='caption' color='error'>
              {mediaError}
            </Typography>
          )}
          {media.length === 0 && !mediaError && (
            <Typography variant='body2' color='text.secondary'>
              No media items yet. Add at least 1 image (max 5).
            </Typography>
          )}
          {media.map((item, index) => (
            <MediaItemRow
              key={item.id}
              item={item}
              index={index}
              total={media.length}
              onMoveUp={() => moveMediaItem(index, 'up')}
              onMoveDown={() => moveMediaItem(index, 'down')}
              onRemove={() => removeMediaItem(item.id)}
              onTextChange={(text) => updateMediaText(item.id, text)}
              onImageFile={(file) => handleImageFile(item.id, file)}
            />
          ))}
          <Stack direction='row' alignItems='center' spacing={1}>
            <Button
              startIcon={<AddIcon />}
              variant='outlined'
              onClick={addMediaItem}
              disabled={media.length >= 5}
              sx={{ alignSelf: 'flex-start' }}
            >
              Add Media Item
            </Button>
            {media.length > 0 && (
              <Typography variant='caption' color='text.secondary'>
                {media.length} / 5
              </Typography>
            )}
          </Stack>
        </Stack>
      </SectionCard>

      <Divider sx={{ mb: 3 }} />

      <Stack direction='row' spacing={2} justifyContent='flex-end'>
        <Button variant='outlined' onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleSubmit}
          disabled={saving}
          startIcon={
            saving ? <CircularProgress size={16} color='inherit' /> : undefined
          }
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Stack>
    </Box>
  );
}

// ─── Media Item Row ───────────────────────────────────────────────────────────

interface MediaItemRowProps {
  item: MediaItem;
  index: number;
  total: number;
  onMoveUp(): void;
  onMoveDown(): void;
  onRemove(): void;
  onTextChange(text: string): void;
  onImageFile(file: File): void;
}

function MediaItemRow({
  item,
  index,
  total,
  onMoveUp,
  onMoveDown,
  onRemove,
  onTextChange,
  onImageFile
}: MediaItemRowProps) {
  const inputId = `media-image-${item.id}`;
  const imageSource = item.preview ?? item.url;

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        alignItems: 'flex-start'
      }}
    >
      {/* Order controls */}
      <Stack alignItems='center' spacing={0.5} sx={{ pt: 0.5 }}>
        <IconButton size='small' onClick={onMoveUp} disabled={index === 0}>
          <KeyboardArrowUpIcon fontSize='small' />
        </IconButton>
        <Typography variant='caption' color='text.secondary' lineHeight={1}>
          {index + 1}
        </Typography>
        <IconButton
          size='small'
          onClick={onMoveDown}
          disabled={index === total - 1}
        >
          <KeyboardArrowDownIcon fontSize='small' />
        </IconButton>
      </Stack>

      {/* Image upload */}
      <Box
        component='label'
        htmlFor={inputId}
        sx={{
          'width': 120,
          'height': 120,
          'flexShrink': 0,
          'border': '2px dashed',
          'borderColor': imageSource ? 'transparent' : 'action.disabled',
          'borderRadius': 1,
          'cursor': 'pointer',
          'display': 'flex',
          'alignItems': 'center',
          'justifyContent': 'center',
          'overflow': 'hidden',
          'transition': 'border-color 0.15s',
          '&:hover': { borderColor: 'primary.main' }
        }}
      >
        {imageSource ? (
          <Box
            component='img'
            src={imageSource}
            alt=''
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Stack alignItems='center' spacing={0.5}>
            <AddPhotoAlternateIcon color='disabled' />
            <Typography variant='caption' color='text.secondary'>
              Upload image
            </Typography>
          </Stack>
        )}
        <input
          id={inputId}
          type='file'
          accept='image/*'
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onImageFile(file);
          }}
        />
      </Box>

      {/* Caption */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <RichTextEditor
          label='Caption / Text'
          value={item.text}
          onChange={onTextChange}
          minHeight={100}
        />
      </Box>

      {/* Remove */}
      <IconButton
        onClick={onRemove}
        color='error'
        size='small'
        sx={{ mt: 0.5 }}
      >
        <DeleteOutlineIcon />
      </IconButton>
    </Box>
  );
}
